// @givmo/board — the Form 990 page's one read (CBG-YR5-VIEW; the backend's `board-form-990.md` §4.1).
//
// ══ FOUR REFUSALS ARE ANSWERS, NOT ERRORS ═════════════════════════════════════════════════════
//
// The read answers four refusals the page words rather than retries, each a settled answer:
//   * 404 — the route isn't served here (an older backend, or the module off): one quiet line;
//   * 409 `board_form_990_unavailable` — the organization's type has no Form 990 page;
//   * 422 `board_form_990_tax_year_unknown` — the address names a tax year that isn't listed;
//   * 422 `board_form_990_year_end_invalid`, or the request validation's own 422 — the address's
//     month and day can't be read as a day the tax year ends.
// A refusal's own sentence is the one shown, when it carries one. Anything else stays an error.
//
// The address is the query: the year by its last day (`tax_year_ends_on`, never its named year — a
// change of year end can give two tax years one name) and, with no fiscal year end recorded, the
// month and day chosen. Nothing is stored anywhere else (I2).

import { useQuery } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { useBoardOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import { decodeForm990, type Form990 } from './form990-decode';

export const FORM_990_REFUSALS = {
  typeHasNoPage: 'board_form_990_unavailable',
  taxYearUnknown: 'board_form_990_tax_year_unknown',
  yearEndInvalid: 'board_form_990_year_end_invalid',
} as const;

/** What the page's address asks for. */
export interface Form990Address {
  taxYearEndsOn: string | null;
  yearEndMonth: number | null;
  yearEndDay: number | null;
}

export type Form990Read =
  | { kind: 'page'; page: Form990 }
  | { kind: 'not-served' }
  | { kind: 'type-refused'; sentence: string | null }
  | { kind: 'tax-year-unknown'; sentence: string | null }
  | { kind: 'year-end-refused'; sentence: string | null };

/** A refusal the page answers in words; the Schedule O draft's read answers the same four. */
export type Form990Refusal = Exclude<Form990Read, { kind: 'page' }>;

/** The page's query keys, rooted per organization. */
export function boardForm990Keys(orgRef: string | null) {
  const root = ['board-form-990', orgRef ?? ''] as const;
  return {
    root,
    page: (address: Form990Address) =>
      [...root, address.taxYearEndsOn, address.yearEndMonth, address.yearEndDay] as const,
  };
}

/** A refusal's own sentence (its `detail`), when it carries one. */
function sentenceOf(error: unknown): string | null {
  if (!isGivmoError(error)) return null;
  const detail = error.entries[0]?.detail;
  return typeof detail === 'string' && detail.trim().length > 0 ? detail.trim() : null;
}

/** A failed read the page answers in words, or `null` for an error. */
export function refusalRead(error: unknown, address: Form990Address): Form990Refusal | null {
  if (isNotServed(error)) return { kind: 'not-served' };
  if (!isGivmoError(error)) return null;
  const code = firstErrorCode(error);
  if (error.status === 409 && code === FORM_990_REFUSALS.typeHasNoPage) {
    return { kind: 'type-refused', sentence: sentenceOf(error) };
  }
  if (error.status !== 422) return null;
  if (code === FORM_990_REFUSALS.taxYearUnknown) {
    return { kind: 'tax-year-unknown', sentence: sentenceOf(error) };
  }
  if (code === FORM_990_REFUSALS.yearEndInvalid) {
    return { kind: 'year-end-refused', sentence: sentenceOf(error) };
  }
  // The request validation's own 422 (a month or day outside its bounds) names no Board code: the
  // address's month and day are what it refused, so the picker asks again in Board's words.
  if (address.yearEndMonth !== null || address.yearEndDay !== null) {
    return { kind: 'year-end-refused', sentence: null };
  }
  return null;
}

export function useForm990(orgRef: string | null, address: Form990Address, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardForm990Keys(orgRef).page(address),
    enabled: enabled && orgRef !== null,
    // The refusals above resolve as answers, so only a failure reaches here: retried once.
    retry: 1,
    queryFn: async (): Promise<Form990Read> => {
      try {
        const { data } = await operations.officerGetBoardForm990({
          path: { org_ref: orgRef ?? '' },
          query: {
            ...(address.taxYearEndsOn === null ? {} : { tax_year_ends_on: address.taxYearEndsOn }),
            ...(address.yearEndMonth === null ? {} : { year_end_month: address.yearEndMonth }),
            ...(address.yearEndDay === null ? {} : { year_end_day: address.yearEndDay }),
          },
          throwOnError: true,
        });
        const page = decodeForm990(data);
        if (page === null) throw new Error('The Form 990 read could not be decoded.');
        return { kind: 'page', page };
      } catch (error) {
        const answered = refusalRead(error, address);
        if (answered !== null) return answered;
        throw error;
      }
    },
  });
}
